import * as React from 'react';
import { Route, Routes } from 'react-router';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import * as microsoftTeams from '@microsoft/teams-js';
import * as TeamsState from './store/TeamsState';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from './store';
import { SignInTab } from './components/SignInTab';
import { Home, HomeNonAdmin } from './components/Home';
import { StatusTab, StatusTabEnum } from './components/StatusTab';
import { ConsultantsTab } from './components/Consultants/ConsultantsTab';
import { CustomersTab } from './components/Customers/CustomersTab';
import { ProjectsTab } from './components/Projects/ProjectsTab';
import { ProjectsAssigmentsTab } from './components/Projects/ProjectsAssigmentsTab';
import { TimeSheetsTab } from './components/Projects/TimeSheetsTab';
import { Layout } from './components/Layout';
import { useTranslation } from 'react-i18next';
import { InvoicesTab } from './components/Invoices/InvoicesTab';
import { ProductsTab } from './components/Products/ProductsTab';
import { SalesTab } from './components/Products/SalesTab';
import { ConsultantsInvoicesTab } from './components/Consultants/ConsultantsInvoicesTab';
import { EID } from './components/EID';
import { ContactsTab } from './components/Contacts/ContactsTab';
import { UserLoginState, UserLoginActionCreators } from './store/Login';
import { FluentProvider, PartialTheme, teamsDarkTheme, teamsDarkV21Theme, teamsHighContrastTheme, teamsLightTheme, teamsLightV21Theme, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { ConsultantsDocumentsPreFilter, ConsultantsDocumentsTab } from './components/ConsultantsDocuments/ConsultantsDocumentsTab';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { TokenHelper } from './TokenHelper';
import { ConsultantActionCreators } from './store/consultants';
import { useTabStyles } from './components/Styles/Editor';
import { PeppolSettings } from './components/Tools/Settings';
import { Logs } from './components/Tools/Logs';
import { NeomyticConsultantID } from './interfaces/Consultant';
import { dataActionCreators } from './store/Data';

function getTheme(themeString: string): PartialTheme {
    switch (themeString) {
        case "teamsDarkV21Theme":
            return teamsDarkV21Theme;
        case "teamsLightV21Theme":
            return teamsLightV21Theme;
        case "webDarkTheme":
            return webDarkTheme;
        case "webLightTheme":
            return webLightTheme;
        case "dark":
            return teamsDarkTheme;
        case "contrast":
            return teamsHighContrastTheme;
        default:
            return teamsLightTheme;
    }
}
export function isThemeDark(themeString: string): boolean {
    switch (themeString) {
        case "teamsDarkV21Theme":
        case "webDarkTheme":
        case "dark":
            return true;
        case "contrast":
            return false;
        case "webLightTheme":
        default:
            return false;
    }
}

function getMainStyle(themeString: string): React.CSSProperties {
    switch (themeString) {
        case "teamsDarkV21Theme":
        case "webDarkTheme":
        case "dark":
        case "contrast":
            return { height: "100%", colorScheme: "dark" };
    }
    return { height: "100%", colorScheme: "light" };
}


export const App = () => {
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const dispatch = useDispatch(); //useDispatch<React.Dispatch<TeamsState.KnownAction>>();
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const { i18n } = useTranslation();
    //const [themeString, setThemeString] = useSelector((state: ApplicationState) => state.teamsState.themeString);//React.useState('webLightTheme');
    const styles = useTabStyles();
    const [hubCon, setHubCon] = React.useState<HubConnection>();
    console.log(teamsState);
    React.useEffect(() => {
        if (hubCon === undefined && (isAuthenticated || teamsState.InTeams)) {
            const connection: HubConnection = new HubConnectionBuilder()
                .withUrl("/hub", {
                    accessTokenFactory: () => {
                        return TokenHelper.GetToken(instance, teamsState);
                    }
                })
                .withAutomaticReconnect()
                .build();

            connection.on("messageReceived", (username: string, message: string) => {
                console.log("username", username);
                console.log("message", message);
            });
            connection.on("DocumentUpdate", (id: string) => {
                console.log("SignalR DocumentUpdate", id);
                dispatch(ConsultantActionCreators.requestConsultantDocument(id, instance, teamsState));
            });
            connection.on("PeppolReceivedInvoice", (id: string) => {
                console.log("SignalR PeppolReceivedInvoice", id);
                dispatch(ConsultantActionCreators.RequestConsultantPeppolReceivedInvoices(instance, teamsState));
            });
            connection.on("DocumentDeleted", (id: string) => {
                console.log("SignalR DocumentDeleted", id);
                dispatch(ConsultantActionCreators.requestConsultantDocumentdeletion_id(id));
            });
            connection.on("TimeSheetEntry", (id: string) => {
                console.log("SignalR TimeSheetEntry", id);
                dispatch(dataActionCreators.reloadTimeSheetEntry(instance, teamsState, id));
            });
            connection.on("Sale", (id: string) => {
                console.log("SignalR Sale", id);
                // TODO handle sale dispatch(dataActionCreators.reloadInvoice(instance, teamsState, id));
            });
            connection.on("Invoice", (id: string) => {
                console.log("SignalR Invoice", id);
                dispatch(dataActionCreators.reloadInvoice(instance, teamsState, id));
            });
            connection.onreconnected((connectionId?: string) => {
                console.log("SignalR reconnected connectionId=", connectionId);
            });
            connection.onreconnecting((error?: Error) => {
                console.log("SignalR reconnecting error=", error);
            });
            connection.onclose((error?: Error) => {
                console.log("SignalR close error=", error);
                //    setTimeout(() => {
                //        connection.start().then(() => console.log('SignalR Reconnection started.......!')).catch(err => console.error("Reconnect failed:", err));
                //    }, 5000);
            });
            connection.start()
                .then(() => console.log('SignalR Connection started.......!'))
                .catch((err: string) => console.log('SignalR Error while connect with server', err));

            setHubCon(connection);
        }
    }, [instance, teamsState, isAuthenticated]);

    React.useEffect(() => {
        // Create an scoped async function in the hook
        async function TryInitTeams() {
            // Check for the Microsoft Teams SDK object.
            if (microsoftTeams) {
                try {
                    // Initialize the Microsoft Teams SDK
                    await microsoftTeams.app.initialize();
                    // If there is a context, we are in Microsoft Teams
                    const context: microsoftTeams.app.Context = await microsoftTeams.app.getContext();
                    if (null !== context) {

                        if (context.app.theme !== undefined) {
                            dispatch({ type: 'SET_THEMESTRING', themeString: context.app.theme });
                            //setThemeString(context.app.theme);
                        }
                        dispatch({ type: 'SET_TEAMS_CONFIG', teamsConfig: context });

                        const locale = context.app.locale;
                        console.log("i18n", i18n);
                        if (i18n && i18n.changeLanguage) {
                            i18n.changeLanguage(locale ? locale.substr(0, 2) : "en");
                        }
                        microsoftTeams.app.registerOnThemeChangeHandler((theme) => {
                            dispatch({ type: 'SET_THEMESTRING', themeString: theme });
                            //setThemeString(theme);
                        });

                        microsoftTeams.app.notifySuccess();
                    }
                    else {
                        console.log("microsoftTeams.app.Context Error");
                        dispatch({ type: 'SET_NOT_IN_TEAMS' });
                    }
                }
                catch (e) {
                    console.log("microsoftTeams.app.initialize Exception", e);
                    dispatch({ type: 'SET_NOT_IN_TEAMS' });
                }
            }
            else {
                console.log("microsoftTeams null");
                dispatch({ type: 'SET_NOT_IN_TEAMS' });
            }
        }
        // Execute the created function directly
        TryInitTeams();
    }, []);

    React.useEffect(() => {
        if ((isAuthenticated || teamsState.InTeams) && UserLoginState && UserLoginState.rigths === null && !UserLoginState.isLoading) {
            dispatch(UserLoginActionCreators.requestUserLogin(instance, teamsState));
        }
    }, [isAuthenticated, teamsState, UserLoginState]);
    React.useEffect(() => {
        if (teamsState.InTeams && UserLoginState && UserLoginState.rigths !== null && !UserLoginState.isLoading && UserLoginState.rigths.isAdmin && !UserLoginState.graphTokenLoading && UserLoginState.graphToken === undefined) {
            dispatch(UserLoginActionCreators.requestGraphToken(instance, teamsState));
        }
    }, [UserLoginState]);

    // If running in Teams or the authentication succeed, let go...
    if (teamsState.InTeams === null) {
        //const spinner = {
        //    keyframe: {
        //        from: {
        //            transform: 'rotate(0deg)',
        //        },
        //        to: {
        //            transform: 'rotate(360deg)',
        //        },
        //    },
        //    duration: '5s',
        //    iterationCount: 'infinite',
        //}

        //return (
        //    <Provider
        //        theme={{
        //            animations: {
        //                spinner,
        //            },
        //        }}
        //    >
        //        <Animation name="spinner">
        //            <img src={NeomyticLogo } />
        //        </Animation>
        //    </Provider>
        //)
        // we don't know if running in Teams or web
        return <>Loading...</>;
    }

    if (!isAuthenticated && !teamsState.InTeams) {
        // Since not authenticated nor in Teams, ask for login
        return <FluentProvider theme={getTheme(teamsState.themeString)} className={styles.providerSignInTab} style={getMainStyle(teamsState.themeString)}>
            <SignInTab />
        </FluentProvider>;
    }

    if (UserLoginState.isLoading) {
        // User has not the roght to use the app
        return <FluentProvider theme={getTheme(teamsState.themeString)} className={styles.providerSignInTab} style={getMainStyle(teamsState.themeString)}>
            <StatusTab MsgId={StatusTabEnum.Loading} />
        </FluentProvider>;
    }

    if (UserLoginState.rigths && (!UserLoginState.rigths.isAdmin && !UserLoginState.rigths.canEnterTimeSheet && !UserLoginState.rigths.canEnterDocuments)) {
        // User has not the right to use the app
        return <FluentProvider theme={getTheme(teamsState.themeString)} className={styles.providerSignInTab} style={getMainStyle(teamsState.themeString)}>
            <StatusTab MsgId={StatusTabEnum.AccessDenied} />
        </FluentProvider>;
    }

    return <FluentProvider theme={getTheme(teamsState.themeString)} style={getMainStyle(teamsState.themeString)}>
        <Layout>
            <Routes>
                <Route path='/' element={<Home />} />
                <Route path='/HomeNonAdmin' element={<HomeNonAdmin />} />
                <Route path='/Home' element={<Home />} />
                <Route path='/Contacts' element={<ContactsTab />} />
                <Route path='/Consultants' element={<ConsultantsTab />} />
                <Route path='/ConsultantsInvoices' element={<ConsultantsInvoicesTab />} />
                <Route path='/ConsultantsDocumentsPrivateMyBuys' element={<ConsultantsDocumentsTab privateDocumentsOnly={true} preFilter={ConsultantsDocumentsPreFilter.Buys} />} />
                <Route path='/ConsultantsDocumentsPrivateMySells' element={<ConsultantsDocumentsTab privateDocumentsOnly={true} preFilter={ConsultantsDocumentsPreFilter.Sells} />} />
                <Route path='/ConsultantsDocumentsPrivate' element={<ConsultantsDocumentsTab privateDocumentsOnly={true} />} />
                <Route path='/ConsultantsDocuments' element={<ConsultantsDocumentsTab privateDocumentsOnly={false} />} />
                <Route path='/CustomersFromConsultants' element={<CustomersTab ForceLoggedInConsultant={true} />} />
                <Route path='/Customers' element={<CustomersTab ForceLoggedInConsultant={false} />} />
                <Route path='/ProjectsAssigments' element={<ProjectsAssigmentsTab />} />
                <Route path='/Projects' element={<ProjectsTab />} />
                <Route path='/PrivateTimeSheet' element={<TimeSheetsTab ForceLoggedInConsultant={true} />} />
                <Route path='/TimeSheet' element={<TimeSheetsTab ForceLoggedInConsultant={false} />} />
                <Route path='/NeomyticDocuments' element={<ConsultantsDocumentsTab privateDocumentsOnly={false} PreFilterConsultantid={NeomyticConsultantID} />} />
                <Route path='/NeomyticDocumentsBuys' element={<ConsultantsDocumentsTab privateDocumentsOnly={false} PreFilterConsultantid={NeomyticConsultantID} preFilter={ConsultantsDocumentsPreFilter.Buys} />} />
                <Route path='/Invoices' element={<InvoicesTab viewCreditNotes={false} />} />
                <Route path='/CreditNotes' element={<InvoicesTab viewCreditNotes={true} />} />
                <Route path='/tabPersonal' element={<Home />} />
                <Route path='/Products' element={<ProductsTab />} />
                <Route path='/Sales' element={<SalesTab />} />
                <Route path='/EID' element={<EID />} />
                <Route path='/PeppolSettings' element={<PeppolSettings />} />
                <Route path='/Logs' element={<Logs />} />
            </Routes>
        </Layout>
    </FluentProvider>;
};